// src/features/treinos/utils/workoutXp.js
//
// Cálculo centralizado do XP de treino — PREVISTO e GANHO base — em UM lugar,
// determinístico a partir dos PARÂMETROS DO PLANO (exercícios/séries/duração
// estimada), NUNCA das cargas reais logadas. Por construção: previsto == ganho.
//
// • As cargas reais continuam indo só pro WORKOUT_LOG (evolução). NÃO entram
//   aqui. Os dois fluxos não se cruzam.
// • O servidor (functions/vita/xpVita.js) é a FONTE DA VERDADE do XP. Aqui só
//   ESPELHAMOS a fórmula pra calcular previsto/ganho no cliente, sem forkar.
//
// ── CONTRATO PRA PHASE D (documentação; NÃO implementar agora) ───────────────
//  1) buildPredictedAtividade(workout) é o contrato que a Phase D vai reusar
//     pra PRÉ-PREENCHER o check-in. Usando-o, o ganho base do check-in fica
//     idêntico ao previsto base (mesma key + mesma durationMin → mesmo points
//     → mesmo XP). Garanta que a Phase D monte `atividades` a partir dele.
//  2) O bônus ao vivo CONTA no ranking (decisão de produto). Logo, ao
//     finalizar→check-in (Phase D), o bônus deve ser somado ao xpEarned pelo
//     caminho normal do servidor (awardXpEventInternal → updateCompetition-
//     ParticipantXp), para entrar no XP_DELTA. Isso é Phase D — aqui só
//     calculamos o valor do bônus pra UI/preview.

import {
  calcPoints,
  POINTS_CONFIG,
  getActivityByKey,
} from "./pointsConfig.js";
import { getWorkoutDurationEstimate } from "../components/muscle-map/muscleMapUtils.js";

// ─────────────────────────────────────────────────────────────────────────────
// 1) ESPELHO da fórmula do servidor
//
// ESPELHO de functions/vita/xpVita.js:170 calcTreinoXp — manter sincronizado.
// Servidor é a fonte da verdade.
//   server: Math.max(15, Math.min(80, 15 + Math.floor(pointsTotal / 8)))
// Constantes nomeadas num só lugar pra facilitar o sync.
// ─────────────────────────────────────────────────────────────────────────────
export const XP_MIN = 15;
export const XP_MAX = 80;
export const POINTS_DIVISOR = 8;

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Espelho verbatim de calcTreinoXp(pointsTotal) do servidor.
 * @param {number} pointsTotal soma dos `points` das atividades
 * @returns {number} XP de treino (clampeado em [XP_MIN, XP_MAX])
 */
export function mirrorCalcTreinoXp(pointsTotal = 0) {
  const points = safeNumber(pointsTotal);
  return Math.max(XP_MIN, Math.min(XP_MAX, XP_MIN + Math.floor(points / POINTS_DIVISOR)));
}

// ─────────────────────────────────────────────────────────────────────────────
// Key de força/musculação em pointsConfig.js
// `musculacao`: wDur=4.0, wDist=0, wCal=0.060, wSteps=0, mult=1.20 → a duração
// estimada DOMINA o cálculo de pontos (sem distância/passos; calorias opcionais).
// ─────────────────────────────────────────────────────────────────────────────
export const STRENGTH_ACTIVITY_KEY = "musculacao";

/**
 * Monta a(s) atividade(s) derivada(s) do PLANO no MESMO shape que o check-in
 * grava (ver RegistrarTreinoCheckin.jsx handleSaveOrAddActivity), usando a key
 * de força e getWorkoutDurationEstimate(workout) para `duration_min`.
 *
 * Retorna um ARRAY (o check-in trabalha com `atividades[]`). Hoje é sempre uma
 * única atividade de musculação; o formato de lista deixa a porta aberta.
 *
 * CONTRATO Phase D: use isto pra pré-preencher o check-in → ganho base == previsto base.
 *
 * @param {Array|Object} workout treino/plano (mesmos shapes aceitos por exerciseList)
 * @returns {Array<Object>} atividades no shape do check-in
 */
export function buildPredictedAtividade(workout) {
  const durationMin = getWorkoutDurationEstimate(workout);
  const act = getActivityByKey(STRENGTH_ACTIVITY_KEY);

  const points = calcPoints({
    key: STRENGTH_ACTIVITY_KEY,
    durationMin,
    distanceKm: 0,
    calories: 0,
    steps: 0,
  });

  return [
    {
      activity_key: STRENGTH_ACTIVITY_KEY,
      activity_label: act?.label ?? "Musculação",
      category: act?.category ?? "Musculação",
      duration_min: durationMin,
      distance_km: 0,
      calories: 0,
      steps: 0,
      points,
      config: POINTS_CONFIG[STRENGTH_ACTIVITY_KEY] || POINTS_CONFIG.outro,
    },
  ];
}

/** Mapeia uma atividade (shape do check-in, snake) → input de calcPoints (camel). */
function atividadeToCalcPointsInput(a) {
  return {
    key: a.activity_key,
    durationMin: a.duration_min,
    distanceKm: a.distance_km,
    calories: a.calories,
    steps: a.steps,
  };
}

/**
 * XP base PREVISTO de um treino, determinístico a partir do plano.
 * = mirrorCalcTreinoXp( Σ calcPoints(buildPredictedAtividade(workout)) ).
 * Recalcula os pontos via calcPoints (igual ao servidor, que soma a.points).
 *
 * @param {Array|Object} workout
 * @returns {number} XP base
 */
export function calculateWorkoutBaseXp(workout) {
  const atividades = buildPredictedAtividade(workout);
  const pointsTotal = atividades.reduce(
    (sum, a) => sum + calcPoints(atividadeToCalcPointsInput(a)),
    0,
  );
  return mirrorCalcTreinoXp(pointsTotal);
}

// ─────────────────────────────────────────────────────────────────────────────
// 4) Bônus por treino AO VIVO
//
// UMA constante, fácil de ajustar. Conta no ranking (XP_DELTA) — ver contrato
// Phase D no topo. Default: 10% do base, mínimo +10, arredondado.
// ─────────────────────────────────────────────────────────────────────────────
export const LIVE_WORKOUT_BONUS = { type: "percent", value: 0.10, min: 10 }; // TODO produto: confirmar magnitude. Conta no ranking (XP_DELTA).

// Marcadores que indicam sessão "ao vivo". Os nomes exatos vêm da Phase D.
// TODO Phase D: fixar o marcador real da sessão do ModoTreino.
const LIVE_FLAG_FIELDS = ["live", "isLive", "ao_vivo", "aoVivo", "AO_VIVO"];
const SESSION_MARKER_FIELDS = ["sessaoId", "SESSAO_ID", "sessionId"];

function has(obj, fields) {
  return obj && fields.some((f) => obj[f] !== undefined && obj[f] !== null);
}

/** True se a sessão foi feita ao vivo (tolerante; default = ao vivo se parecer sessão real). */
function isLiveWorkoutSession(workoutSession) {
  if (!workoutSession) return false;
  // flag explícita tem prioridade
  for (const f of LIVE_FLAG_FIELDS) {
    if (workoutSession[f] === true) return true;
    if (workoutSession[f] === false) return false;
  }
  // sem flag: considera ao vivo se tiver marcador de sessão real
  return has(workoutSession, SESSION_MARKER_FIELDS);
}

/** Extrai o objeto de treino de dentro de uma sessão (ou devolve o próprio). */
function resolveWorkout(workoutSession) {
  if (!workoutSession) return workoutSession;
  return (
    workoutSession.workout ||
    workoutSession.treino ||
    workoutSession.TREINO ||
    workoutSession
  );
}

/**
 * Bônus de XP por treino ao vivo (inteiro). 0 se não foi ao vivo.
 * @param {Object} workoutSession sessão do treino (contém/aponta o workout + marcador live)
 * @returns {number}
 */
export function calculateLiveWorkoutBonus(workoutSession) {
  if (!isLiveWorkoutSession(workoutSession)) return 0;
  const base = calculateWorkoutBaseXp(resolveWorkout(workoutSession));
  const pct = Math.round(base * LIVE_WORKOUT_BONUS.value);
  return Math.max(LIVE_WORKOUT_BONUS.min, pct);
}

/**
 * XP detalhado de um treino concluído, pra UI: { base, liveBonus, total }.
 *
 * NÃO inclui streak bonus: o streak (+10 a cada 3 dias, +30 a cada 7) é aplicado
 * SERVER-SIDE em awardXpEventInternal (functions/vita/xpVita.js) e depende do
 * estado do usuário (LAST_ACTIVITY_DATE/FROZEN_DAYS), não do treino em si. A UI
 * deve mostrar o streak separadamente se/quando aplicável.
 *
 * @param {Object} workoutSession
 * @returns {{ base: number, liveBonus: number, total: number }}
 */
export function calculateCompletedWorkoutXp(workoutSession) {
  const base = calculateWorkoutBaseXp(resolveWorkout(workoutSession));
  const liveBonus = calculateLiveWorkoutBonus(workoutSession);
  return { base, liveBonus, total: base + liveBonus };
}
